export interface InputState {
  left: boolean;
  right: boolean;
  shoot: boolean;
}

export interface KeyboardInput {
  readonly state: InputState;
  install(target?: EventTarget): void;
  uninstall(): void;
}

interface KeyEventLike {
  code?: string;
  preventDefault?: () => void;
}

const TRACKED: Record<string, keyof InputState | undefined> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Space: 'shoot',
};

export function createKeyboardInput(): KeyboardInput {
  const state: InputState = { left: false, right: false, shoot: false };
  let attached: EventTarget | null = null;

  const setFor = (code: string | undefined, value: boolean): boolean => {
    if (!code) return false;
    const key = TRACKED[code];
    if (!key) return false;
    state[key] = value;
    return true;
  };

  const onKeyDown = (event: Event): void => {
    const e = event as KeyEventLike;
    if (setFor(e.code, true)) {
      e.preventDefault?.();
    }
  };

  const onKeyUp = (event: Event): void => {
    const e = event as KeyEventLike;
    if (setFor(e.code, false)) {
      e.preventDefault?.();
    }
  };

  const uninstall = (): void => {
    if (!attached) return;
    attached.removeEventListener('keydown', onKeyDown);
    attached.removeEventListener('keyup', onKeyUp);
    attached = null;
  };

  const install = (target?: EventTarget): void => {
    uninstall();
    const t = target ?? (typeof window !== 'undefined' ? window : null);
    if (!t) return;
    attached = t;
    t.addEventListener('keydown', onKeyDown);
    t.addEventListener('keyup', onKeyUp);
  };

  return {
    state,
    install,
    uninstall,
  };
}
